import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, pairwise, startWith } from 'rxjs/operators';
import { cloneDeep } from 'lodash-es';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Features } from '@nimbus-assistant/enums/features';
import { PreCallInfo } from '@nimbus-assistant/enums/pre-call-info';
import { TaskState, ITask } from '@nimbus-assistant/models/task.model';
import { Modality } from '@nimbus-assistant/models/task-types.model';
import { RepositionCurrentWindowMessage } from '@nimbus-assistant/models/web-view-messages/reposition-current-window-message';
import {
  ASSISTANT_EXTENDED_TOAST_TABS_CONTEXT,
  ASSISTANT_EXTENDED_TOAST_TABS_FLOW,
  ITaskTab,
} from '@nimbus-assistant/models/task-details-tabs';
import { SettingsService } from './settings.service';
import { UserSettingsService } from './user-settings.service';
import { MessageService } from './message.service';
import { LoggingService } from './logging.service';
import { AssistantTasksService } from './assistant-tasks.service';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class TaskToastService {
  private currentlyExtending = false;
  private lastTeamsToastHeight = 0;

  public readonly isExtendingTeamsToast$ = new BehaviorSubject<boolean>(false);
  public readonly extendedTabItems$ = new BehaviorSubject<ITaskTab[]>(null);

  public constructor(
    private readonly tasksService: AssistantTasksService,
    private readonly settingsService: SettingsService,
    private readonly userSettingsService: UserSettingsService,
    private readonly loggingService: LoggingService,
  ) {
    this.watchIncomingTask();
    this.watchTaskTransitions();
  }

  /** When a new incoming audio service call arrives (and no task is already connected), start extending the toast.
   *  Uses tasks$ (not incomingTask$) so it re-fires when callFlow data is patched. */
  private watchIncomingTask(): void {
    combineLatest([this.tasksService.tasks$, this.tasksService.connectedTask$])
      .pipe(
        map(([tasks, connected]) => {
          if (connected) {
            return null;
          }
          return tasks.find((t) => t.state === TaskState.Ringing && t.isServiceCall && t.modality === Modality.Audio) ?? null;
        }),
        untilDestroyed(this),
      )
      .subscribe((incoming) => {
        if (incoming && this.userSettingsService.userSettings?.onTopTeamToast) {
          this.startExtendingTeamsToast(incoming);
        }
      });
  }

  /** When ringing stops (accepted, declined, or task gone), stop extending */
  private watchTaskTransitions(): void {
    combineLatest([this.tasksService.incomingTask$, this.tasksService.connectedTask$])
      .pipe(
        map(([incoming, _connected]) => !!incoming),
        distinctUntilChanged(),
        startWith(false),
        pairwise(),
        untilDestroyed(this),
      )
      .subscribe(([wasRinging, isRinging]) => {
        if (wasRinging && !isRinging) {
          this.endExtendingTeamsToast();
        }
      });
  }

  private startExtendingTeamsToast(task: ITask): void {
    const callFlowLength = task.callFlow?.length ?? 0;
    const preCallInfoEnabled = this.settingsService.supportsFeature(Features.PreCallInfo);
    const showContextTab = preCallInfoEnabled && this.userSettingsService.userSettings?.preCallInfo === PreCallInfo.Info;

    const CONTEXT_TAB_ROW_COUNT = 7;
    const ROW_HEIGHT_PX = 40;
    const BASE_HEIGHT_PX = 100;

    const rowsNumber = showContextTab ? CONTEXT_TAB_ROW_COUNT : callFlowLength;
    const calculatedHeight = rowsNumber * ROW_HEIGHT_PX + BASE_HEIGHT_PX;
    const maxExtendingHeight = 260;
    const windowHeight = Math.min(calculatedHeight, maxExtendingHeight);

    this.loggingService.Info(
      `TaskToastService: startExtending — callFlowLength: ${callFlowLength}, showContextTab: ${showContextTab}, rowsNumber: ${rowsNumber}, windowHeight: ${windowHeight}, currentlyExtending: ${this.currentlyExtending}`,
    );

    if (!this.currentlyExtending || windowHeight !== this.lastTeamsToastHeight) {
      this.extendedTabItems$.next(cloneDeep(showContextTab ? ASSISTANT_EXTENDED_TOAST_TABS_CONTEXT : ASSISTANT_EXTENDED_TOAST_TABS_FLOW));

      MessageService.SendExtendTeamsToastMessage(windowHeight, true);
      MessageService.SendBringToFrontMessage();
      this.isExtendingTeamsToast$.next(true);
      this.currentlyExtending = true;
      this.lastTeamsToastHeight = windowHeight;
    }
  }

  private endExtendingTeamsToast(): void {
    if (this.currentlyExtending) {
      MessageService.SendExtendTeamsToastMessage(0, false);
      MessageService.SendMessage(new RepositionCurrentWindowMessage(false));
      MessageService.SendBringToFrontMessage();
      this.isExtendingTeamsToast$.next(false);
      this.extendedTabItems$.next(null);
      this.currentlyExtending = false;
    }
  }
}
